/* eslint-disable react/prop-types */
import React, { useEffect, useImperativeHandle, useMemo, useState } from 'react'

import { useCombobox } from 'downshift'

import * as Separator from '@radix-ui/react-separator'

import {
  ArrowClockwise, ArrowDown, CheckCircle, CursorText, X,
} from 'react-bootstrap-icons'

import slugify from 'slugify'
import debounce from 'lodash.debounce'

import { ComboBoxProps } from './ComboBox.types'

import './ComboBox.scss'
import ButtonGroup from '../ButtonGroup/ButtonGroup'
import ScrollZone from '../ScrollZone/ScrollZone'

const ICON_SIZE = 18

export const ComboBox: React.ForwardRefExoticComponent<ComboBoxProps> = React.forwardRef(
  function ComboBox ({
    items,
    renderItem,
    setSelected,
    onClear,
    onScrollReachEnd,
    stateReducer = (state, { type, changes }) => changes,
    header,
    footer,
    inputStyle,
    menuStyle,
    inputProps,
    disabled = false,
    hidden = false,
    loading = false,
    icon = <CursorText />,
    maxHeight = 180,
    noMatchText = 'No matching items',
  }, ref) {
    const [inputItems, setInputItems] = useState(items)

    useEffect(() => {
      setInputItems(items)
    }, [items])

    const debouncedOnScrollReachEnd = useMemo(() => 
      onScrollReachEnd ?
        debounce(onScrollReachEnd, 1000 , { leading:true, trailing: false })
        : undefined
    , [onScrollReachEnd])

    const {
      isOpen,
      getToggleButtonProps,
      getMenuProps,
      getInputProps,
      getComboboxProps,
      highlightedIndex,
      getItemProps,
      openMenu,
      closeMenu,
      selectItem,
      selectedItem,
      inputValue,
      setInputValue,
    } = useCombobox({
      items: inputItems,
      onInputValueChange: ({ inputValue }) => {
        setInputItems(
          items.filter((item) => slugify(item.name, { lower: true }).includes(slugify(inputValue, { lower: true }))),
        )
      },
      itemToString: (item) => (item ? item.name : ''),
      onIsOpenChange: (changes) => {
        if(changes.isOpen){
          setInputItems(items)
        }
      },
      stateReducer
      // to what extent is this necessary?
      // environment: environment ? environment : undefined
    })

    useImperativeHandle(ref, () => ({
      selectItem,
      selectedItem,
      inputValue,
      setInputValue,
    }))

    useEffect(() => {
      
      if (!selectedItem) {
        setInputValue('')
      } else {
        // @ts-ignore
        setInputValue(selectedItem.key ?? selectedItem.name)
      }
      
      setSelected && setSelected(selectedItem)

      closeMenu()
    }, [closeMenu, selectedItem, setInputValue, setSelected])

    return (
      <div data-testid="ComboBoxSelect" className={hidden ? undefined : 'poll-combobox'}>
        <ButtonGroup
          wrapperProps={{
            ...getComboboxProps(),
            style:{ 
              ...inputStyle, 
              position: 'relative', 
              width: '100%' 
            }
          }}
          disabled={disabled}
        >
          <input
            {...getInputProps({
              placeholder: 'Search for something...',
              spellCheck:false,
              disabled,
              style:{
                paddingLeft: 42,
                borderTopLeftRadius: 4,
                borderBottomLeftRadius: isOpen ? 0 : 4,
                flex: 12,
                lineHeight: '1.5rem',
              },
              onReset: () => {
                selectItem(undefined)
              },
              ...inputProps,
              onFocus: () => {
                openMenu()
              },
              type: hidden ? 'hidden' : undefined
            })}
          />
          {!hidden &&
            <i 
              style={{
                position: 'absolute',
                height: '100%',
                width: 42,
                justifyContent: 'center',
                alignItems: 'center',
                display: 'flex',
                color: 'white',
                // zIndex: 20,
              }}
            >
              {
                selectedItem ? 
                  <CheckCircle size={ICON_SIZE} />
                  :
                  React.cloneElement(icon, { size: ICON_SIZE })
              }
            </i>
          }
          {(() => {
            if (hidden) {
              return <></>
            } else if (loading) {
              return(<button
                type='button'
                tabIndex={-1}
                disabled={disabled}
                style={{
                  borderBottomRightRadius: isOpen ? 0 : 4,
                }}
              >
                <ArrowClockwise className="spin" size={ICON_SIZE} />
              </button>)
            } else if (selectedItem) {
              return (
                <button
                  type='button'
                  tabIndex={-1}
                  disabled={disabled}
                  onClick={() => {
                    selectItem(null)
                    setSelected && setSelected(undefined)
                    onClear && onClear()
                  }}
                  style={{
                    borderBottomRightRadius: isOpen ? 0 : 4,
                  }}
                >
                  <X size={ICON_SIZE} />
                </button>
              )
            } else {
              return (
                <button
                  type='button'  
                  disabled={disabled}
                  {...getToggleButtonProps()}
                  style={{
                    borderBottomRightRadius: isOpen ? 0 : 4,
                  }}
                >
                  <ArrowDown size={ICON_SIZE} />
                </button>
              )
            }
          })()}
        </ButtonGroup>
        { !hidden &&
          <div className="poll-combobox-content"
            style={{
              maxHeight: isOpen ? maxHeight : 0,
              boxShadow: isOpen ? 'rgba(0, 0, 0, 0.08) 0px 2px 4px' : undefined,
              padding: isOpen ? '6px 4px' : 0,
              visibility: isOpen ? undefined : 'hidden'
            }}
          >
            {header &&
              <div className='poll-combo-header-container'
                style={{
                  opacity: isOpen ? 1 : 0
                }}
              >
                {header}
                {<Separator.Root key="poll-separator" className="poll-separator" />}
              </div>
            }
            <ScrollZone 
              isOpen={isOpen}
              items={inputItems}
              highlightedIndex={highlightedIndex}
              menuStyle={menuStyle}
              renderItem={renderItem}
              getMenuProps={getMenuProps}
              getItemProps={getItemProps}
              onScrollEnd={debouncedOnScrollReachEnd}
            />
            <div className='poll-combo-footer-container'
              style={{
                opacity: isOpen ? 1 : 0
              }}
            >
              {<Separator.Root key="poll-separator" className="poll-separator" />}
              {loading && 
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'var(--slate3)',
                    color: 'var(--slate10)',
                    padding: '6px 0px',
                  }}
                > 
                  Loading...
                </div>
              }
              {inputItems.length > 0 && !loading && footer}
              {inputItems.length < 1 && !loading &&
                <div
                  key="footer"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--slate10)',
                    padding: '10px 0px',
                  }}
                >
                  {noMatchText}
                </div>
              }
            </div>
          </div>
        }
      </div>
    )
  })

export default ComboBox
